const JSON_OBJECT_START_PATTERN = /\{\s*\\*"/
const PYTHON_MESSAGE_PATTERN =
  /\\?'message\\?'\s*:\s*(?:"((?:\\.|[^"\\])*)"|'((?:\\.|[^'\\])*)')/
const MAX_DEPTH = 10

const getErrorMessages = (error: unknown): string[] => {
  const { graphQLErrors, message } = (error ?? {}) as {
    graphQLErrors?: unknown
    message?: unknown
  }
  const messages = (Array.isArray(graphQLErrors) ? graphQLErrors : [])
    .map((graphQLError: { message?: unknown } | null) => graphQLError?.message)
    .filter((text): text is string => typeof text === 'string' && text !== '')
  if (messages.length > 0) return messages
  return typeof message === 'string' && message ? [message] : []
}

const withoutQuoteEscapes = (text: string): string =>
  text.replace(/\\([\s\S])/g, (escape, char) => (char === "'" ? char : escape))

const parseEscapedJson = (text: string): unknown => {
  let candidate = withoutQuoteEscapes(text)
  for (let level = 0; level < MAX_DEPTH; level += 1) {
    try {
      return JSON.parse(candidate)
    } catch {
      try {
        candidate = withoutQuoteEscapes(JSON.parse(`"${candidate}"`))
      } catch {
        return undefined
      }
    }
  }
  return undefined
}

const stringFieldIn = (
  value: unknown,
  key: string,
  depth = 0
): string | undefined => {
  if (!value || typeof value !== 'object' || depth >= MAX_DEPTH)
    return undefined
  const field = (value as Record<string, unknown>)[key]
  if (typeof field === 'string' && field) return field
  for (const child of Object.values(value)) {
    const found = stringFieldIn(child, key, depth + 1)
    if (found) return found
  }
  return undefined
}

const jsonMessageOf = (text: string): string | undefined => {
  const start = text.search(JSON_OBJECT_START_PATTERN)
  const end = text.lastIndexOf('}')
  if (start === -1 || end < start) return undefined
  const json = parseEscapedJson(text.slice(start, end + 1))
  return (
    stringFieldIn(json, 'ps_api_response_message') ??
    stringFieldIn(json, 'message')
  )
}

const pythonMessageOf = (text: string): string | undefined => {
  const match = text.match(PYTHON_MESSAGE_PATTERN)
  const message = match?.[1] ?? match?.[2]
  return message?.replace(/\\(['"\\])/g, '$1') || undefined
}

const innermostMessageOf = (text: string, depth = 0): string | undefined => {
  const message = depth < MAX_DEPTH ? jsonMessageOf(text) : undefined
  if (message === undefined) return pythonMessageOf(text)
  return innermostMessageOf(message, depth + 1) ?? message
}

export const getLaunchErrorDetails = (error: unknown): string | undefined => {
  const messages = getErrorMessages(error)
  if (messages.length === 0) return undefined
  return messages
    .map((message) => innermostMessageOf(message) ?? message)
    .join('\n')
}
